'use client'

import { motion } from 'framer-motion'
import { InfoIcon, Share2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { RollingNumber } from '@/components/ui/rolling-number'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import {
  HOME_LIST,
  HOME_ROW,
  HOME_SOFT_SPRING,
  HomeMeter,
  HomePanel,
} from '@/features/home/components/home-motion'
import { HomeShareSavingsDialog } from '@/features/home/components/home-share-savings'
import { useHomeTradeStats } from '@/features/home/hooks/use-home-trade-stats'
import { useNumberFormat } from '@/hooks/use-number-format'
import { getExchangeIconKey, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'

type WindowDays = '7' | '30'

function Explain({ children }: { children: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={children}
          className="text-foreground/30 hover:text-foreground cursor-help transition-colors"
        >
          <InfoIcon className="size-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">{children}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The user's own trading (Aaron's ask): volume over the last 7 or 30 days, day by day and by
 * venue, with the maker share — and next to it what trading through OrderX saved, kept to one
 * number on Alex's call: the taker fee avoided on every maker fill (at the account's fee tier).
 * It shares as a card, like a PnL card.
 */
export function HomeTrading({ className }: { className?: string }) {
  const t = useTranslations('trade.home.trading')
  const fmt = useNumberFormat()
  const [windowDays, setWindowDays] = useState<WindowDays>('30')
  const { stats, isPending, hasAccounts } = useHomeTradeStats(windowDays === '7' ? 7 : 30)
  const peak = Math.max(1, ...(stats?.daily.map((day) => day.volumeUsd) ?? [1]))
  const [shareOpen, setShareOpen] = useState(false)
  // Against the window's USD volume, so savings compare across account sizes.
  const bps = (usd: number) =>
    stats && stats.volumeUsd > 0
      ? `${fmt.number((usd / stats.volumeUsd) * 10_000, { maximumFractionDigits: 1 })} bps`
      : ''
  const empty = !isPending && (!hasAccounts || !stats || stats.fillCount === 0)

  return (
    <>
      <HomePanel
        title={t('title')}
        className={cn('col-span-12 lg:col-span-8', className)}
        aside={
          <SegmentedControl
            layoutId="home-trading-window"
            size="xs"
            value={windowDays}
            onChange={setWindowDays}
            options={[
              { value: '7', label: t('windows.7') },
              { value: '30', label: t('windows.30') },
            ]}
          />
        }
      >
        {isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : empty ? (
          <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
        ) : (
          stats && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <div className="flex min-w-0 flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-foreground/50 text-xs">{t('volume')}</span>
                  <RollingNumber
                    text={fmt.usd(stats.volumeUsd, { sensitive: true })}
                    className="font-display text-foreground text-3xl leading-tight tracking-tight"
                  />
                  <span className="text-foreground/45 text-xs">
                    {t('fills', { count: stats.fillCount })}
                    {stats.optionContracts > 0 &&
                      ` · ${t('options', { count: fmt.number(stats.optionContracts, { maximumFractionDigits: 2 }) })}`}
                  </span>
                </div>
                {/* Day by day: bars rise in one after another, the latest on the right. */}
                <div className="flex h-28 items-end gap-[3px]" role="img" aria-label={t('daily')}>
                  {stats.daily.map((day, index) => (
                    <Tooltip key={day.dayMs}>
                      <TooltipTrigger asChild>
                        <motion.span
                          className={cn(
                            'min-w-0 flex-1 cursor-default rounded-t-[2px]',
                            day.volumeUsd > 0
                              ? 'bg-shell-accent/60 hover:bg-shell-accent'
                              : 'bg-foreground/10'
                          )}
                          initial={{ height: 0 }}
                          animate={{ height: `${Math.max(2, (day.volumeUsd / peak) * 100)}%` }}
                          transition={{ ...HOME_SOFT_SPRING, delay: index * 0.015 }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {new Date(day.dayMs).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                        })}
                        {' · '}
                        {fmt.usd(day.volumeUsd, { sensitive: true })}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </div>
              <div className="flex min-w-0 flex-col gap-3">
                {stats.makerShare !== null && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-foreground/50 flex items-center justify-between text-xs">
                      {t('makerShare')}
                      <span className="text-foreground">
                        {fmt.number(stats.makerShare * 100, { maximumFractionDigits: 0 })}%
                      </span>
                    </span>
                    <HomeMeter value={stats.makerShare} barClassName="bg-chart-mint" />
                  </div>
                )}
                <span className="text-foreground/50 text-xs">{t('byVenue')}</span>
                <motion.ul
                  variants={HOME_LIST}
                  initial="hidden"
                  animate="show"
                  className="flex flex-col gap-2"
                >
                  {stats.byVenue.map((venue) => (
                    <motion.li
                      key={venue.exchange}
                      variants={HOME_ROW}
                      className="flex flex-col gap-1"
                    >
                      <span className="flex items-center gap-2 text-xs">
                        <ExchangeIcon
                          exchange={getExchangeIconKey(venue.exchange) ?? ''}
                          className="size-4"
                        />
                        <span className="text-foreground/80">
                          {getExchangeLabel(venue.exchange)}
                        </span>
                        <span className="text-foreground ml-auto">
                          {fmt.usd(venue.volumeUsd, { sensitive: true })}
                        </span>
                      </span>
                      <HomeMeter
                        value={stats.volumeUsd > 0 ? venue.volumeUsd / stats.volumeUsd : 0}
                        barClassName="bg-foreground/60"
                      />
                    </motion.li>
                  ))}
                </motion.ul>
              </div>
            </div>
          )
        )}
      </HomePanel>

      <HomePanel
        title={t('savings.title')}
        className={cn('col-span-12 lg:col-span-4', className)}
        aside={
          <>
            <span className="bg-ox-gold/10 text-ox-gold rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase">
              {t('savings.estimate')}
            </span>
            {stats && stats.feeSavingsUsd > 0 && (
              <Button size="xs" variant="outline" onClick={() => setShareOpen(true)}>
                <Share2Icon className="size-3" />
                {t('share.button')}
              </Button>
            )}
          </>
        }
      >
        {isPending ? (
          <Skeleton className="h-36 w-full" />
        ) : empty || !stats ? (
          <p className="text-foreground/50 py-6 text-sm">{t('savings.empty')}</p>
        ) : (
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-foreground/50 flex items-center gap-1.5 text-xs">
                {t('savings.total', { days: windowDays })}
                <Explain>{t('savings.makerFeesHelp')}</Explain>
              </span>
              <RollingNumber
                text={fmt.usd(stats.feeSavingsUsd, { sensitive: true })}
                className="font-display text-chart-mint text-3xl leading-tight tracking-tight"
              />
              <span className="text-foreground/45 text-xs">
                {t('savings.rate', { bps: bps(stats.feeSavingsUsd) })}
              </span>
            </div>
            <ul className="flex flex-col gap-2.5 text-xs">
              {stats.makerShare !== null && (
                <li className="flex flex-col gap-1.5">
                  <span className="flex items-center justify-between">
                    <span className="text-foreground/70">{t('savings.makerVolume')}</span>
                    <span className="text-foreground">
                      {fmt.usd(stats.volumeUsd * stats.makerShare, { sensitive: true })}
                      <span className="text-foreground/40">
                        {' '}
                        · {fmt.number(stats.makerShare * 100, { maximumFractionDigits: 0 })}%
                      </span>
                    </span>
                  </span>
                  <HomeMeter value={stats.makerShare} barClassName="bg-chart-mint" />
                </li>
              )}
              <li className="border-foreground/10 flex items-center gap-1.5 border-t pt-2.5">
                <span className="text-foreground/70">{t('savings.feesPaid')}</span>
                <span className="text-foreground ml-auto">
                  {fmt.usd(stats.feesPaidUsd, { sensitive: true })}
                </span>
                <span className="text-foreground/40 w-14 text-right">{bps(stats.feesPaidUsd)}</span>
              </li>
            </ul>
            <p className="text-foreground/35 mt-auto text-[11px] leading-4">
              {t('savings.footnote')}
            </p>
          </div>
        )}
      </HomePanel>
      {stats && (
        <HomeShareSavingsDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          data={{
            days: Number(windowDays),
            savedUsd: stats.feeSavingsUsd,
            volumeUsd: stats.volumeUsd,
            makerShare: stats.makerShare,
            fillCount: stats.fillCount,
          }}
        />
      )}
    </>
  )
}
